import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Trash2 } from "lucide-react";
import { ReportExtractionItemType } from "../../../models/reportExtraction";
import {
  SOC1_COLUMN_LABELS,
  titleCaseFromSnake,
} from "../../../lib/soc1TabLabels";

const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value.map((v) => String(v)).join(", ");
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  return String(value);
};

interface ReportExtractionTableProps {
  items: ReportExtractionItemType[];
  onCellClick: (
    item: ReportExtractionItemType,
    index: number | null,
    fieldName: string | null,
  ) => void;
  /** When provided, renders a per-row delete button (hover-visible). */
  onDeleteItem?: (itemId: string) => Promise<void>;
  selectedIndex: number | null;
}

interface ColumnDef {
  key: string;
  label: string;
}

// Row default height in "compact" state. Any row can be resized freely by
// dragging its bottom edge; only clamped below at MIN_ROW_HEIGHT.
const COMPACT_ROW_HEIGHT = 84;
const MIN_ROW_HEIGHT = 32;
const RESIZE_HANDLE_HEIGHT = 6;
const CELL_MIN_WIDTH_PX = 200;
const COLUMN_RESIZE_HANDLE_WIDTH = 6;
const MIN_COLUMN_WIDTH_PX = 80;

interface RowDragState {
  rowId: string;
  startY: number;
  startHeight: number;
}

interface ColumnDragState {
  colKey: string;
  startX: number;
  startWidth: number;
}

export const ReportExtractionTable = ({
  items,
  onCellClick,
  onDeleteItem,
  selectedIndex,
}: ReportExtractionTableProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const headerCellRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [rowHeightOverrides, setRowHeightOverrides] = useState<
    Record<string, number>
  >({});
  const [columnWidthOverrides, setColumnWidthOverrides] = useState<
    Record<string, number>
  >({});
  const [dragState, setDragState] = useState<RowDragState | null>(null);
  const [columnDragState, setColumnDragState] =
    useState<ColumnDragState | null>(null);

  // Columns in template order: labelled keys first (per SOC1_COLUMN_LABELS),
  // then any unlabelled data keys appended alphabetically.
  const columns: ColumnDef[] = useMemo(() => {
    if (items.length === 0) return [];

    // Underscore-prefixed keys are backend metadata (e.g. `_field_citations`)
    // and must not render as a column.
    const itemKey = items[0].itemKey;
    const allKeys = new Set<string>();
    for (const item of items) {
      for (const key of Object.keys(item.data)) {
        if (key.startsWith("_")) continue;
        // Page Number is temporarily hidden while extraction accuracy is fixed.
        if (itemKey === "control_objectives" && key === "page_number") continue;
        allKeys.add(key);
      }
    }
    const labels = SOC1_COLUMN_LABELS[itemKey] ?? {};
    const labelledKeys = Object.keys(labels).filter((k) => allKeys.has(k));
    const unlabelledKeys = Array.from(allKeys)
      .filter((k) => !(k in labels))
      .sort();
    return [...labelledKeys, ...unlabelledKeys].map((key) => ({
      key,
      label: labels[key] ?? titleCaseFromSnake(key),
    }));
  }, [items]);

  // Row-height drag. Document-level listeners so the drag survives when
  // the cursor leaves the table. Clamped only below at MIN_ROW_HEIGHT.
  useEffect(() => {
    if (!dragState) return;
    const onMove = (e: MouseEvent) => {
      const delta = e.clientY - dragState.startY;
      const nextHeight = Math.max(
        MIN_ROW_HEIGHT,
        Math.round(dragState.startHeight + delta),
      );
      setRowHeightOverrides((prev) => {
        if (prev[dragState.rowId] === nextHeight) return prev;
        return { ...prev, [dragState.rowId]: nextHeight };
      });
    };
    const onUp = () => setDragState(null);
    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [dragState]);

  // Column-width drag. Same document-level pattern as row-height drag,
  // clamped below at MIN_COLUMN_WIDTH_PX.
  useEffect(() => {
    if (!columnDragState) return;
    const onMove = (e: MouseEvent) => {
      const delta = e.clientX - columnDragState.startX;
      const nextWidth = Math.max(
        MIN_COLUMN_WIDTH_PX,
        Math.round(columnDragState.startWidth + delta),
      );
      setColumnWidthOverrides((prev) => {
        if (prev[columnDragState.colKey] === nextWidth) return prev;
        return { ...prev, [columnDragState.colKey]: nextWidth };
      });
    };
    const onUp = () => setColumnDragState(null);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [columnDragState]);

  // Scroll the currently-selected row into view (driven by keyboard nav
  // in the parent).
  useEffect(() => {
    if (selectedIndex === null || !items[selectedIndex]) return;
    const rowEl = rowRefs.current.get(items[selectedIndex].id);
    if (rowEl) {
      rowEl.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [selectedIndex, items]);

  const getRowHeight = useCallback(
    (rowId: string): number => rowHeightOverrides[rowId] ?? COMPACT_ROW_HEIGHT,
    [rowHeightOverrides],
  );

  const handleResizeMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    rowId: string,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const rowEl = rowRefs.current.get(rowId);
    if (!rowEl) return;
    setDragState({
      rowId,
      startY: e.clientY,
      startHeight: rowEl.getBoundingClientRect().height,
    });
  };

  // Measure the header cell's actual rendered width at drag start so
  // flex-sized columns have an accurate baseline.
  const handleColumnResizeMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    colKey: string,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const headerEl = headerCellRefs.current.get(colKey);
    if (!headerEl) return;
    const currentWidth = headerEl.getBoundingClientRect().width;
    setColumnDragState({
      colKey,
      startX: e.clientX,
      startWidth: currentWidth,
    });
  };

  // Column style: user-resized columns are fixed-width; unresized columns
  // flex to share available space with a shared minimum.
  const columnStyle = (colKey: string): React.CSSProperties => {
    const override = columnWidthOverrides[colKey];
    if (override !== undefined) {
      return {
        flex: `0 0 ${override}px`,
        width: `${override}px`,
        minWidth: `${override}px`,
      };
    }
    return {
      flex: `1 1 ${CELL_MIN_WIDTH_PX}px`,
      minWidth: `${CELL_MIN_WIDTH_PX}px`,
    };
  };

  if (items.length === 0) {
    return (
      <div className="text-center text-stone-500 mt-8">
        No extracted items found.
      </div>
    );
  }

  // Min table width. When it exceeds the container, the outer wrapper
  // horizontal-scrolls.
  const minTableWidth = columns.reduce(
    (sum, col) => sum + (columnWidthOverrides[col.key] ?? CELL_MIN_WIDTH_PX),
    0,
  );

  return (
    <div ref={containerRef} className="w-full overflow-x-auto">
      <div
        role="table"
        className="border border-stone-300 rounded-md overflow-hidden bg-white"
        style={{ minWidth: `${minTableWidth}px` }}
      >
        {/* Header row */}
        <div
          role="row"
          className="flex bg-stone-50 border-b border-stone-300 text-xs font-medium text-stone-700 uppercase tracking-wide"
        >
          {columns.map((col, colIdx) => (
            <div
              key={col.key}
              ref={(el) => {
                if (el) headerCellRefs.current.set(col.key, el);
                else headerCellRefs.current.delete(col.key);
              }}
              role="columnheader"
              className={
                "relative px-3 py-2.5 " +
                (colIdx < columns.length - 1 ? "border-r border-stone-200" : "")
              }
              style={columnStyle(col.key)}
            >
              {col.label}
              {/* Column resize handle — thin vertical strip straddling the
                  right border of the header cell. Cursor becomes
                  col-resize on hover; a blue tint appears while hovering
                  the hot-zone so users can find it. The last column has
                  no divider to resize against, so we skip its handle. */}
              {colIdx < columns.length - 1 && (
                <div
                  className="absolute top-0 bottom-0 cursor-col-resize z-20 hover:bg-blue-400/30 transition-colors"
                  style={{
                    right: `-${COLUMN_RESIZE_HANDLE_WIDTH / 2}px`,
                    width: `${COLUMN_RESIZE_HANDLE_WIDTH}px`,
                  }}
                  onMouseDown={(e) => handleColumnResizeMouseDown(e, col.key)}
                  aria-hidden
                />
              )}
            </div>
          ))}
        </div>

        {/* Data rows */}
        {items.map((item, index) => {
          const rowHeight = getRowHeight(item.id);
          const isSelected = index === selectedIndex;
          return (
            <div
              key={item.id}
              ref={(el) => {
                if (el) rowRefs.current.set(item.id, el);
                else rowRefs.current.delete(item.id);
              }}
              role="row"
              className={
                "group relative flex border-b border-stone-200 " +
                (isSelected ? "bg-blue-50" : "bg-white hover:bg-stone-50")
              }
              style={{ height: `${rowHeight}px` }}
            >
              {columns.map((col, colIdx) => {
                const raw = item.data[col.key];
                const text = formatCellValue(raw);
                const isLastColumn = colIdx === columns.length - 1;
                return (
                  <div
                    key={col.key}
                    role="cell"
                    className={
                      "relative cursor-pointer text-sm text-stone-900 " +
                      (isLastColumn ? "" : "border-r border-stone-200")
                    }
                    style={columnStyle(col.key)}
                    onClick={() => onCellClick(item, index, col.key)}
                  >
                    {/* overflow: hidden lives on this inner wrapper (not the
                        cell) so the column-resize handle positioned at
                        right: -3px on the cell doesn't get clipped by the
                        cell's clipping context. */}
                    <div className="px-3 py-2 h-full overflow-hidden">
                      <div
                        data-cell-content
                        className="whitespace-pre-wrap break-words leading-normal"
                      >
                        {text}
                      </div>
                    </div>
                    {/* Column-resize handle mirrored on every data cell so
                        the drag zone extends the full height of the column
                        divider (not just the header). onMouseDown stops
                        propagation so it does not fire the cell-click
                        citation handler. */}
                    {!isLastColumn && (
                      <div
                        className="absolute top-0 bottom-0 cursor-col-resize z-20 hover:bg-blue-400/30 transition-colors"
                        style={{
                          right: `-${COLUMN_RESIZE_HANDLE_WIDTH / 2}px`,
                          width: `${COLUMN_RESIZE_HANDLE_WIDTH}px`,
                        }}
                        onMouseDown={(e) =>
                          handleColumnResizeMouseDown(e, col.key)
                        }
                        onClick={(e) => e.stopPropagation()}
                        aria-hidden
                      />
                    )}
                  </div>
                );
              })}
              {/* Row-bottom drag handle — grabs the divider between this
                  row and the next. Absolutely positioned at the bottom
                  edge so it lives in a 6px strip; cursor changes on hover
                  and the strip lights up blue to signal drag affordance. */}
              <div
                className="absolute left-0 right-0 bottom-0 cursor-ns-resize z-10 hover:bg-blue-400/30 transition-colors"
                style={{ height: `${RESIZE_HANDLE_HEIGHT}px` }}
                onMouseDown={(e) => handleResizeMouseDown(e, item.id)}
                aria-hidden
              />
              {/* Delete button — only rendered when the parent wired
                  ``onDeleteItem`` (i.e. legacy flag-off mode). Shows on row
                  hover; stops event propagation so it doesn't fire cell
                  citation navigation. */}
              {onDeleteItem && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void onDeleteItem(item.id);
                  }}
                  className="absolute right-2 top-2 z-30 p-1 rounded text-stone-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Delete item"
                  aria-label="Delete item"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
